import { Box, Flex, FormControl, FormLabel, SimpleGrid, Stack, Switch, Tab, TabList, TabPanel, TabPanels, Tabs, Text } from '@chakra-ui/react';
import { PageContentWrapper, PageSubtitleLight, PageTitle, TextNormal } from 'custom-style-components';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { format, getDay } from 'date-fns';
import { getDate, getSaoPauloDate, renderDateRange } from 'utils/dateUtils';

import { ActivityType } from 'types/models';
import { CancelTokenSource } from 'axios';
import ErrorMessage from 'components/ErrorMessage';
import LoadingSpinner from 'components/LoadingSpinner';
import Navbar from 'components/Navbar';
import ScheduleActivityCard from './components/ScheduleActivityCard';
import createCancelTokenSource from 'utils/createCancelTokenSource';
import { fetchManyActivitiesByEvent } from 'services/fetch/activities';
import ptBR from 'date-fns/locale/pt-BR';
import renderEventName from 'utils/renderEventName';
import { useAuth } from 'hooks/auth';
import { useEvent } from 'hooks/EventProvider';
import { useLocation } from 'react-router';

const EventSchedulesPage: React.FC = () => {
    const { pathname } = useLocation();
    const { event, loading: eventLoading, error: eventError } = useEvent();
    const { user } = useAuth();

    const [fetchActivitiesError, setFetchActivitiesError] = useState(false);
    const [loading, setLoading] = useState<boolean>(true);
    const [moreActivities, setMoreActivities] = useState(false);

    const [activities, setActivities] = useState<ActivityType[]>([]);
    const [userActivities, setUserActivities] = useState<ActivityType[]>([]);

    const [showOnlyMyActivities, setShowOnlyMyActivities] = useState(false);
    const [hideSoldOut, setHideSoldOut] = useState(false);
    const [pendingRatings, setPendingRatings] = useState(false);

    const cancelTokenRef = useRef<CancelTokenSource | null>(null);

    const loadActivities = (silent = false) => {
        if (cancelTokenRef.current) {
            cancelTokenRef.current.cancel('Operation canceled due to new request.');
        }
        cancelTokenRef.current = createCancelTokenSource();
        const source = cancelTokenRef.current;

        if (event && event.id) {
            if (!silent) setLoading(true);

            const activitiesPromise = fetchManyActivitiesByEvent(
                source.token,
                event.id.toString(),
                { limit: Number.MAX_SAFE_INTEGER }
            );

            const userActivitiesPromise = user
                ? fetchManyActivitiesByEvent(
                    source.token,
                    event.id.toString(),
                    { limit: Number.MAX_SAFE_INTEGER, fromUser: user.id }
                )
                : Promise.resolve({ activities: [] as ActivityType[], totalCount: 0 });

            Promise.all([activitiesPromise, userActivitiesPromise])
                .then(([activitiesRes, userActivitiesRes]) => {
                    const acts = activitiesRes.activities;
                    const userActs = userActivitiesRes.activities;

                    const mergedActs = acts.map(act => {
                        const userAct = userActs.find(ua => ua.id === act.id);
                        if (userAct && userAct.activityRegistration) {
                            return { ...act, activityRegistration: userAct.activityRegistration };
                        }
                        return act;
                    });

                    setActivities(mergedActs);
                    setUserActivities(userActs);

                    setPendingRatings(
                        userActs.some(
                            (x) =>
                                x.activityRegistration &&
                                x.activityRegistration.some(
                                    (y) => y.rating === 0
                                )
                        )
                    );
                })
                .catch((err) => {
                    if (err.message !== 'Operation canceled due to new request.') {
                        setFetchActivitiesError(err.message);
                    }
                })
                .finally(() => {
                    setLoading(false);
                });
        }
    };

    useEffect(() => {
        if (event && event.endDate) {
            if (new Date(event.endDate) > getDate() && !event.statusActive) {
                setLoading(false);
                setMoreActivities(true);
                return;
            }
            setMoreActivities(false);
            loadActivities();
        }

        return () => {
            if (cancelTokenRef.current) {
                cancelTokenRef.current.cancel('Component unmounted.');
            }
        };
    }, [event]);

    const filteredActivities = useMemo(() => {
        let result = [...activities];

        if (showOnlyMyActivities) {
            result = result.filter(a => userActivities.some(ua => ua.id === a.id));
        }

        if (hideSoldOut) {
            result = result.filter(a => (a.totalRegistry ?? 0) < a.vacancy);
        }

        // Sort by start time
        result.sort((a, b) => {
            const timeA = new Date(a.schedules[0].startDate).getTime();
            const timeB = new Date(b.schedules[0].startDate).getTime();
            return timeA - timeB;
        });

        return result;
    }, [activities, userActivities, showOnlyMyActivities, hideSoldOut]);

    const renderTabElements = () => {
        if (!activities || activities.length === 0) {
            return <ErrorMessage>Nenhuma atividade cadastrada.</ErrorMessage>;
        }

        const daysOfWeek = ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'];

        // Determine which day should be selected by default (the first one with activities)
        let defaultIndex = 0;
        if (activities.length > 0 && activities[0].schedules && activities[0].schedules.length > 0) {
            defaultIndex = getDay(getSaoPauloDate(new Date(activities[0].schedules[0].startDate)));
        }

        return (
            <Tabs
                isFitted
                size="lg"
                colorScheme="blue"
                defaultIndex={defaultIndex}
                variant="enclosed"
            >
                <TabList mt={6} mb={6} overflowX="auto" overflowY="hidden" whiteSpace="nowrap">
                    {daysOfWeek.map((day, index) => {
                        const dayActivities = filteredActivities.filter((activity) => {
                            if (!activity.schedules || activity.schedules.length === 0) return false;
                            return getDay(getSaoPauloDate(new Date(activity.schedules[0].startDate))) === index;
                        });

                        if (dayActivities.length > 0) {
                            const formattedDate = format(
                                getSaoPauloDate(new Date(dayActivities[0].schedules[0].startDate)),
                                'dd \'de\' MMM',
                                { locale: ptBR }
                            );
                            return (
                                <Tab
                                    key={index}
                                    fontSize={{ base: 'xl', md: '2xl' }}
                                    fontWeight="bold"
                                    color="gray.600"
                                    _selected={{ color: 'blue.600', bg: 'white', borderTopColor: 'blue.500', borderTopWidth: '4px' }}
                                    bg="gray.100"
                                >
                                    <Box textAlign="center" py={2}>
                                        <Box>{day}</Box>
                                        <Box fontSize="xl" fontWeight="normal" color="gray.500" mt={1}>{formattedDate}</Box>
                                    </Box>
                                </Tab>
                            );
                        } else {
                            return (
                                <Tab key={index} isDisabled fontSize={{ base: 'xl', md: '2xl' }} color="gray.300">
                                    <Box textAlign="center" py={2}>
                                        <Box>{day}</Box>
                                    </Box>
                                </Tab>
                            );
                        }
                    })}
                </TabList>

                <TabPanels>
                    {daysOfWeek.map((day, dayIndex) => {
                        const dayActivities = filteredActivities.filter((activity) => {
                            if (!activity.schedules || activity.schedules.length === 0) return false;
                            return getDay(getSaoPauloDate(new Date(activity.schedules[0].startDate))) === dayIndex;
                        });

                        return (
                            <TabPanel key={dayIndex} p={0}>
                                {dayActivities.length > 0 ? (
                                    <SimpleGrid columns={{ base: 1, lg: 2, xl: 2 }} spacing={6} p={4} alignItems="stretch">
                                        {dayActivities.map((activity) => {
                                            const isRegistered = !!userActivities.find(ua => ua.id === activity.id);
                                            return (
                                                <ScheduleActivityCard
                                                    key={activity.id}
                                                    event={event!}
                                                    activity={activity}
                                                    alreadyRegistered={isRegistered}
                                                    refreshActivities={() => loadActivities(true)}
                                                />
                                            );
                                        })}
                                    </SimpleGrid>
                                ) : (
                                    <Box textAlign="center" py={10}>
                                        <ErrorMessage>Nenhuma atividade para este dia.</ErrorMessage>
                                    </Box>
                                )}
                            </TabPanel>
                        );
                    })}
                </TabPanels>
            </Tabs>
        );
    };

    return (
        <>
            <Navbar activeItemUrl={pathname} />
            <Box bg="gray.100" minH="100vh" py={8}>
                <PageContentWrapper>
                    <Box textAlign="center" mb={8}>
                        <PageTitle>{event ? renderEventName(event) : 'Programação'}</PageTitle>
                        <PageSubtitleLight>
                            {event && renderDateRange(new Date(event.registryStartDate), new Date(event.registryEndDate))}
                        </PageSubtitleLight>
                        <Box color="gray.500" fontSize="lg" mt={2}>
                            Todos os horários estão no fuso horário de Brasília (GMT-3)
                        </Box>
                    </Box>

                    {pendingRatings && (
                        <Box bg="orange.50" p={4} borderRadius="lg" borderLeftWidth="4px" borderLeftColor="orange.400" mb={8} boxShadow="sm">
                            <TextNormal style={{ color: '#d69e2e', fontWeight: 'bold' }}>
                                Você possui avaliações pendentes em suas atividades!
                            </TextNormal>
                        </Box>
                    )}

                    <Flex justify="center" mb={6} wrap="wrap" gap={8}>
                        <FormControl display="flex" alignItems="center" w="auto">
                            <FormLabel fontSize="2xl" mb="0" color="gray.700" fontWeight="medium">
                                Ocultar esgotadas
                            </FormLabel>
                            <Switch
                                size="lg"
                                colorScheme="blue"
                                isChecked={hideSoldOut}
                                onChange={(e) => setHideSoldOut(e.target.checked)}
                            />
                        </FormControl>

                        {user && (
                            <FormControl display="flex" alignItems="center" w="auto">
                                <FormLabel fontSize="2xl" mb="0" color="gray.700" fontWeight="medium">
                                    Minhas atividades
                                </FormLabel>
                                <Switch
                                    size="lg"
                                    colorScheme="blue"
                                    isChecked={showOnlyMyActivities}
                                    onChange={(e) => setShowOnlyMyActivities(e.target.checked)}
                                />
                            </FormControl>
                        )}
                    </Flex>

                    {eventError ? (
                        <ErrorMessage>{eventError}</ErrorMessage>
                    ) : fetchActivitiesError ? (
                        <ErrorMessage>{fetchActivitiesError}</ErrorMessage>
                    ) : moreActivities ? (
                        <Box textAlign="center" mt={10}>
                            <PageTitle>Em breve!</PageTitle>
                        </Box>
                    ) : eventLoading || (loading && activities.length === 0) ? (
                        <LoadingSpinner />
                    ) : (
                        renderTabElements()
                    )}
                </PageContentWrapper>
            </Box>
        </>
    );
};

export default EventSchedulesPage;
